import { Box, ListItemText, MenuItem, styled } from "@mui/material";
import { colors, GhostPlaceholder, Typography } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import { CORE_PANEL_IDS } from "contexts/sidePanels";
import { useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";

import { useProcoreStamp } from "./useProcoreStamp";

const StyledMenuItem = styled(MenuItem)({
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
});

export function ProcoreMobileContextMenuStamp() {
    const { t } = useTranslation();
    const { togglePanel } = useUnifiedPanelToggle();

    const stampData = useProcoreStamp({
        openProperties: () => togglePanel(CORE_PANEL_IDS.PROPERTIES),
    });

    if (!stampData) {
        return null;
    }

    const {
        stamp,
        obj,
        isLoading,
        isOnline,
        flyToDisabled,
        multipleObjectsSelected,
        anyObjectsSelected,
        highlightedCount,
        menuItems,
        hide,
        selectSimilar,
        isolateInXRayMode,
        viewProperties,
        zoomToSelection,
        addSectionPlane,
        executeMenuItem,
    } = stampData;

    return (
        <Box
            sx={{
                pointerEvents: "auto",
                width: 250,
                borderRadius: "4px",
                overflow: "hidden",
                bgcolor: "background.paper",
            }}
        >
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <ListItemText
                    sx={{
                        padding: "12px",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        overflowWrap: "anywhere",
                    }}
                >
                    <Typography intent="h3">
                        {multipleObjectsSelected
                            ? `[${highlightedCount}] Objects Selected`
                            : obj?.name || (stamp.data.object ? `Object ${stamp.data.object}` : "")}
                    </Typography>
                </ListItemText>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={hide} disabled={stamp.data.object === undefined}>
                    <ListItemText>
                        <Typography intent="body">
                            {multipleObjectsSelected ? t("hideMultipleObjects") : t("hideObject")}
                        </Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {multipleObjectsSelected ? null : isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple disabled={!isOnline || !obj?.name} onClick={selectSimilar}>
                    <ListItemText>
                        <Typography intent="body">{t("selectSimilar")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={isolateInXRayMode} disabled={stamp.data.object === undefined}>
                    <ListItemText>
                        <Typography intent="body">{t("isolateInXRayMode")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={viewProperties} disabled={flyToDisabled}>
                    <ListItemText>
                        <Typography intent="body">{t("viewProperties")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {!anyObjectsSelected ? null : isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem
                    disableRipple
                    onClick={() => {
                        void zoomToSelection();
                    }}
                    disabled={flyToDisabled}
                >
                    <ListItemText>
                        <Typography intent="body">{t("zoomToSelection")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {multipleObjectsSelected ? null : isLoading ? (
                <MenuGhostPlaceholder />
            ) : (
                <StyledMenuItem disableRipple onClick={addSectionPlane} disabled={!stamp.data.normal}>
                    <ListItemText>
                        <Typography intent="body">{t("addSectionPlane")}</Typography>
                    </ListItemText>
                </StyledMenuItem>
            )}
            {menuItems.map((item) =>
                isLoading ? (
                    <MenuGhostPlaceholder />
                ) : (
                    <StyledMenuItem disableRipple key={item.id} onClick={() => executeMenuItem(item)}>
                        <ListItemText>
                            <Typography intent="body">{item.label}</Typography>
                        </ListItemText>
                    </StyledMenuItem>
                ),
            )}
        </Box>
    );
}

function MenuGhostPlaceholder() {
    return (
        <Box sx={{ padding: "12px" }}>
            <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
        </Box>
    );
}
